/**
 * KOPS-464: Broker Portal Landing Page
 * aisearchportal.com/broker
 */

import React, { useState } from 'react';
import { Container, Row, Col, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt, faFileContract, faUsers, faDollarSign,
  faRobot, faChartLine, faCheckCircle, faArrowRight,
  faBuilding, faClipboardCheck, faComments, faEye,
  faPhone, faEnvelope, faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';

const colors = {
  navy: '#0F1E35', navy2: '#162844', navy3: '#1E3557',
  gold: '#C9952A', gold2: '#E8B44A', gold3: '#F5D080',
  white: '#FFFFFF', off: '#E8EEF6', muted: '#8FA3BF',
  green: '#27ae60', blue: '#3498db'
};

const BrokerLandingPage: React.FC = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', brokerage: '', agentCount: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Save as host request (emails admin)
      await axiosInstance.post('/events/host-request', {
        contactName: formData.name,
        companyName: formData.brokerage,
        companyType: 'Brokerage',
        email: formData.email,
        phone: formData.phone,
        expectedAttendees: parseInt(formData.agentCount) || null,
        notes: `Broker Portal demo request. Agents: ${formData.agentCount}. ${formData.message}`
      });
      // Also save as contact for CRM tracking
      try {
        await axiosInstance.post('/contacts/sync-single', {
          fullName: formData.name,
          email: formData.email,
          phone: formData.phone,
          brokerage: formData.brokerage,
          source: 'broker_demo_request'
        });
      } catch (e) { /* non-critical */ }
      setSubmitted(true);
    } catch (err) {
      setSubmitted(true); // Show success anyway — they'll get the email
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ background: colors.navy, color: colors.white, fontFamily: "'DM Sans', -apple-system, sans-serif", minHeight: '100vh' }}>
      <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* Nav */}
      <nav style={{ background: colors.navy3, borderBottom: `2px solid ${colors.gold}`, padding: '16px 0' }}>
        <Container className="d-flex justify-content-between align-items-center">
          <a href="/" style={{ textDecoration: 'none' }}>
            <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', letterSpacing: '2px', color: colors.white }}>
              AI<span style={{ color: colors.gold2 }}>Search</span>Portal<span style={{ color: colors.muted, fontSize: '18px' }}>.com</span>
            </span>
          </a>
          <div className="d-flex gap-3 align-items-center">
            <a href="/pricing" style={{ color: colors.muted, textDecoration: 'none', fontSize: '14px', fontWeight: 600 }}>Pricing</a>
            <a href="/events" style={{ color: colors.muted, textDecoration: 'none', fontSize: '14px', fontWeight: 600 }}>Events</a>
            <Button size="sm" href="#demo" style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600 }}>
              Schedule Demo
            </Button>
          </div>
        </Container>
      </nav>

      {/* Hero */}
      <section style={{ background: `linear-gradient(180deg, ${colors.navy} 0%, ${colors.navy2} 100%)`, padding: '80px 0 60px', textAlign: 'center' }}>
        <Container>
          <p style={{ color: colors.gold2, fontSize: '14px', fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '16px' }}>
            For Brokerages & Teams
          </p>
          <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '64px', letterSpacing: '3px', marginBottom: '20px', lineHeight: 1.1, color: colors.white }}>
            Replace Your <span style={{ color: colors.gold2 }}>Entire</span> Tech Stack
          </h1>
          <p style={{ fontSize: '20px', color: colors.muted, maxWidth: '700px', margin: '0 auto 16px' }}>
            Transaction management. AI document review. Commission tracking. Agent CRM. All in one platform.
          </p>
          <p style={{ fontSize: '16px', color: colors.off, maxWidth: '600px', margin: '0 auto 32px' }}>
            Stop paying for dotloop + a CRM + compliance tools separately. Your agents get AI-powered tools. You get oversight and control.
          </p>
          <div style={{ background: colors.navy3, borderLeft: `4px solid ${colors.gold}`, padding: '20px 30px', maxWidth: '500px', margin: '0 auto 32px', borderRadius: '4px' }}>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '48px', color: colors.white }}>
              $500<span style={{ fontSize: '18px', color: colors.muted }}>/mo</span>
            </div>
            <p style={{ color: colors.gold2, fontSize: '14px', margin: '4px 0 0', fontWeight: 600 }}>Includes 2 agent seats | Unlimited free compliance agents | Upgrades $70/mo</p>
          </div>
          <Button size="lg" href="#demo" style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 700, padding: '14px 40px', fontSize: '18px' }}>
            Schedule a Demo <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
          </Button>
        </Container>
      </section>

      {/* Pain Points */}
      <section style={{ background: colors.navy2, padding: '60px 0' }}>
        <Container>
          <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '42px', textAlign: 'center', marginBottom: '40px', color: colors.white }}>
            Sound <span style={{ color: colors.gold2 }}>Familiar?</span>
          </h2>
          <Row className="g-4">
            {[
              { icon: faFileContract, text: 'Your agents submit documents. You spend hours reviewing every page manually.' },
              { icon: faDollarSign, text: 'Commission splits are tracked in spreadsheets. Payout mistakes cost you money and trust.' },
              { icon: faShieldAlt, text: 'Compliance issues surface after closing. By then it\'s too late.' },
              { icon: faUsers, text: 'You\'re paying $500+ for dotloop, another $500 for a CRM, and agents still complain about the tools.' }
            ].map((item, i) => (
              <Col md={6} key={i}>
                <div style={{ background: colors.navy3, borderRadius: '12px', padding: '24px', borderLeft: `4px solid ${colors.gold}`, height: '100%' }}>
                  <FontAwesomeIcon icon={item.icon} style={{ color: colors.gold2, fontSize: '24px', marginBottom: '12px' }} />
                  <p style={{ color: colors.off, fontSize: '15px', margin: 0, lineHeight: 1.7 }}>{item.text}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Features */}
      <section style={{ background: colors.navy, padding: '60px 0' }}>
        <Container>
          <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '42px', textAlign: 'center', marginBottom: '12px', color: colors.white }}>
            One Platform. <span style={{ color: colors.gold2 }}>Everything Included.</span>
          </h2>
          <p style={{ textAlign: 'center', color: colors.muted, marginBottom: '40px' }}>Every feature your brokerage needs. Zero AI inference costs for broker tools.</p>

          <Row className="g-4">
            {[
              { icon: faFileContract, title: 'Transaction Document Management', desc: 'Agents upload contracts and documents. Organized by transaction, accessible from anywhere. Replaces dotloop.', color: colors.blue },
              { icon: faRobot, title: 'AI Document Review', desc: 'KairosIQ Vision reviews every document in real-time. Flags missing signatures, incomplete fields, and compliance issues before you even look at it.', color: colors.green },
              { icon: faDollarSign, title: 'Commission Management', desc: 'Configure splits once. The system calculates payouts for every transaction. No more spreadsheets. No more mistakes.', color: colors.gold },
              { icon: faShieldAlt, title: 'Compliance Dashboard', desc: 'Real-time view of every transaction\'s document status. Know what\'s complete, what\'s missing, and what needs your attention.', color: '#e74c3c' },
              { icon: faComments, title: 'Ask the Broker AI', desc: 'Your agents get instant answers to policy and procedure questions. Train the AI on your brokerage handbook. Available 24/7.', color: '#8e44ad' },
              { icon: faUsers, title: 'Agent Onboarding & Oversight', desc: 'Onboard new agents in minutes. Monitor activity, track performance, and ensure compliance across your entire team.', color: colors.blue }
            ].map((f, i) => (
              <Col md={4} key={i}>
                <div style={{ background: colors.navy2, borderRadius: '12px', padding: '24px', height: '100%', borderTop: `3px solid ${f.color}` }}>
                  <FontAwesomeIcon icon={f.icon} style={{ color: f.color, fontSize: '28px', marginBottom: '12px' }} />
                  <h4 style={{ color: colors.white, fontSize: '18px', fontWeight: 700, marginBottom: '8px' }}>{f.title}</h4>
                  <p style={{ color: colors.muted, fontSize: '14px', margin: 0, lineHeight: 1.6 }}>{f.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* What Agents Get */}
      <section style={{ background: colors.navy2, padding: '60px 0' }}>
        <Container>
          <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '42px', textAlign: 'center', marginBottom: '12px', color: colors.white }}>
            What Your <span style={{ color: colors.gold2 }}>Agents</span> Get
          </h2>
          <p style={{ textAlign: 'center', color: colors.muted, marginBottom: '40px' }}>2 Agent Hub seats included. All agents get free compliance access. Upgrade to AI tools anytime.</p>

          <Row className="g-3 justify-content-center">
            {[
              'Client Hub CRM with MLS search',
              'AI home valuation with comps',
              'Buyer & seller profile management',
              'Email from their Gmail/Outlook',
              'Notes & activity tracking',
              'Agent-branded portal pages',
              'Fair Housing compliant',
              'MLS included'
            ].map((f, i) => (
              <Col md={3} sm={6} key={i}>
                <div style={{ padding: '12px', fontSize: '14px', color: colors.off }}>
                  <FontAwesomeIcon icon={faCheckCircle} className="me-2" style={{ color: colors.gold2 }} />{f}
                </div>
              </Col>
            ))}
          </Row>

          <div className="text-center mt-4">
            <p style={{ color: colors.gold2, fontWeight: 600 }}>
              Upgrade any agent to AI Search ($135/mo) or AI Pro ($185/mo) for AI buyer chat and automation.
            </p>
          </div>
        </Container>
      </section>

      {/* ROI */}
      <section style={{ background: colors.navy, padding: '60px 0' }}>
        <Container>
          <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '42px', textAlign: 'center', marginBottom: '40px', color: colors.white }}>
            The <span style={{ color: colors.gold2 }}>Math</span>
          </h2>
          <Row className="justify-content-center">
            <Col md={8}>
              <div style={{ background: colors.navy2, borderRadius: '12px', overflow: 'hidden' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', borderBottom: `1px solid ${colors.navy3}` }}>
                  <div style={{ padding: '16px 20px', fontWeight: 700, color: colors.muted, fontSize: '13px' }}>What you pay now</div>
                  <div style={{ padding: '16px 20px', fontWeight: 700, color: colors.muted, fontSize: '13px', textAlign: 'center' }}>Monthly</div>
                  <div style={{ padding: '16px 20px', fontWeight: 700, color: colors.muted, fontSize: '13px', textAlign: 'center' }}>With Us</div>
                </div>
                {[
                  { item: 'dotloop / SkySlope', current: '$250-500', ours: 'Included' },
                  { item: 'CRM (kvCORE / FUB)', current: '$500-2,000', ours: 'Included' },
                  { item: 'Compliance tools', current: '$200-500', ours: 'Included' },
                  { item: 'Commission tracking', current: 'Spreadsheets', ours: 'Included' }
                ].map((row, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', borderBottom: `1px solid ${colors.navy3}` }}>
                    <div style={{ padding: '14px 20px', color: colors.off, fontSize: '14px' }}>{row.item}</div>
                    <div style={{ padding: '14px 20px', color: '#e74c3c', fontSize: '14px', textAlign: 'center', fontWeight: 600 }}>{row.current}</div>
                    <div style={{ padding: '14px 20px', color: colors.green, fontSize: '14px', textAlign: 'center', fontWeight: 600 }}>{row.ours}</div>
                  </div>
                ))}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', background: colors.navy3 }}>
                  <div style={{ padding: '16px 20px', color: colors.white, fontWeight: 700 }}>Total (10 agents)</div>
                  <div style={{ padding: '16px 20px', color: '#e74c3c', fontWeight: 700, textAlign: 'center', fontSize: '18px' }}>$1,500-3,500/mo</div>
                  <div style={{ padding: '16px 20px', color: colors.green, fontWeight: 700, textAlign: 'center', fontSize: '18px' }}>$845/mo</div>
                </div>
              </div>
              <p style={{ textAlign: 'center', color: colors.muted, fontSize: '12px', marginTop: '8px' }}>
                $500 broker + 5 included seats + 5 additional at $69 = $845/mo for 10 agents
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Demo Request */}
      <section id="demo" style={{ background: colors.navy2, padding: '60px 0' }}>
        <Container>
          <Row className="justify-content-center">
            <Col md={6}>
              {submitted ? (
                <div className="text-center py-5">
                  <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '48px', color: colors.gold2, marginBottom: '16px' }} />
                  <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '36px', color: colors.white }}>
                    We'll Be In <span style={{ color: colors.gold2 }}>Touch</span>
                  </h3>
                  <p style={{ color: colors.muted }}>Expect a call within 24 hours to schedule your personalized demo.</p>
                </div>
              ) : (
                <>
                  <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '42px', textAlign: 'center', marginBottom: '12px', color: colors.white }}>
                    Schedule a <span style={{ color: colors.gold2 }}>Demo</span>
                  </h2>
                  <p style={{ textAlign: 'center', color: colors.muted, marginBottom: '32px' }}>
                    See how AI Search Portal can replace your entire tech stack in 20 minutes.
                  </p>

                  <Form onSubmit={handleSubmit} style={{ background: colors.navy3, borderRadius: '12px', padding: '32px', border: `1px solid ${colors.navy}` }}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Your Name *</Form.Label>
                          <Form.Control required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                            style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }} />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Brokerage Name *</Form.Label>
                          <Form.Control required value={formData.brokerage} onChange={e => setFormData({ ...formData, brokerage: e.target.value })}
                            style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }} />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Email *</Form.Label>
                          <Form.Control required type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })}
                            style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }} />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Phone *</Form.Label>
                          <Form.Control required value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })}
                            style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }} />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>How many agents in your brokerage?</Form.Label>
                      <Form.Select value={formData.agentCount} onChange={e => setFormData({ ...formData, agentCount: e.target.value })}
                        style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }}>
                        <option value="">Select...</option>
                        <option value="5">1-5 agents</option>
                        <option value="15">6-15 agents</option>
                        <option value="30">16-30 agents</option>
                        <option value="50">31-50 agents</option>
                        <option value="100">50+ agents</option>
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-4">
                      <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Anything else we should know?</Form.Label>
                      <Form.Control as="textarea" rows={3} value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Current tools you're using, biggest pain points, etc."
                        style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }} />
                    </Form.Group>
                    <Button type="submit" disabled={submitting} className="w-100" size="lg"
                      style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 700, fontSize: '16px' }}>
                      {submitting ? <Spinner size="sm" /> : <>Schedule My Demo <FontAwesomeIcon icon={faArrowRight} className="ms-2" /></>}
                    </Button>
                  </Form>
                </>
              )}
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <footer style={{ background: '#0a1628', padding: '40px 0 20px', borderTop: `1px solid ${colors.navy3}` }}>
        <Container>
          <Row>
            <Col md={6}>
              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '24px', letterSpacing: '2px', color: colors.white }}>
                AI<span style={{ color: colors.gold2 }}>Search</span>Portal<span style={{ color: colors.muted, fontSize: '14px' }}>.com</span>
              </span>
              <p style={{ color: colors.muted, fontSize: '13px', marginTop: '8px' }}>
                Built by Arizona Licensed Brokers. Powered by KairosIQ AI, Inc.
              </p>
            </Col>
            <Col md={6} className="text-md-end">
              <p style={{ color: colors.muted, fontSize: '13px' }}>
                KairosIQ AI, Inc. | Chandler, Arizona<br />
                (520) 251-9595 | michael@kairosiq.ai
              </p>
            </Col>
          </Row>
          <hr style={{ borderColor: colors.navy3, margin: '20px 0' }} />
          <p style={{ color: colors.muted, fontSize: '11px', textAlign: 'center', margin: 0 }}>
            AI estimates. Agents decide. Brokers oversee.
          </p>
        </Container>
      </footer>
    </div>
  );
};

export default BrokerLandingPage;
